import { CopilotKit } from "@copilotkit/react-core";
import "@copilotkit/react-ui/styles.css";
import { CopilotPopup as CopilotKitCopilotPopup } from "@copilotkit/react-ui";
import { CopilotChat as CopilotKitCopilotChat } from "@copilotkit/react-ui";
import { useCopilotAction, useCopilotReadable } from "@copilotkit/react-core";

import type { CopilotKitCSSProperties } from "@copilotkit/react-ui";
import { ReactNode } from "react";

const copilotStyles: CopilotKitCSSProperties = {
    "--copilot-kit-primary-color": "hsl(var(--primary))",
    "--copilot-kit-contrast-color": "hsl(var(--primary-foreground))",
    "--copilot-kit-background-color": "hsl(var(--background))",
    "--copilot-kit-secondary-color": "hsl(var(--secondary))",
    "--copilot-kit-secondary-contrast-color": "hsl(var(--secondary-foreground))",
    "--copilot-kit-separator-color": "hsl(var(--border))",
    "--copilot-kit-muted-color": "hsl(var(--muted))",
};

export interface CopilotPopupProps {
    instructions: string;
    labels?: {
        title?: string;
        initial?: string;
        stopGenerating?: string;
        regenerateResponse?: string;
    };
    icons?: {
        openIcon?: ReactNode;
        closeIcon?: ReactNode;
        headerCloseIcon?: ReactNode;
        sendIcon?: ReactNode;
        activityIcon?: ReactNode;
    };
    className?: string;
}

export interface CopilotProviderProps {
    children: ReactNode;
}

/**
 * CopilotProvider - Wrapper component for CopilotKit
 * Use this to wrap your application or a section that needs Copilot functionality
 */
export function CopilotProvider({ children }: CopilotProviderProps) {
    const runtimeUrl = process.env.NEXT_PUBLIC_COPILOT_RUNTIME_URL;
    return (
        <CopilotKit runtimeUrl={runtimeUrl} showDevConsole={false}>
            {children}
        </CopilotKit>
    );
}

/**
 * CopilotPopupButton - A pre-styled CopilotPopup component
 * This component provides a popup interface for the Copilot functionality
 */
export function CopilotPopup({
    instructions,
    labels,
    icons,
    className,
}: CopilotPopupProps) {
    return (
        <div style={copilotStyles} className={className}>
            <CopilotKitCopilotPopup
                instructions={instructions}
                labels={{
                    title: labels?.title || "AI Assistant",
                    initial: labels?.initial || "How can I help you today?",
                    stopGenerating: labels?.stopGenerating || "Stop Generating",
                    regenerateResponse: labels?.regenerateResponse || "Regenerate Response",
                }}
                icons={icons}
            />
        </div>
    );
}

export interface CopilotChatProps {
    instructions: string;
    labels?: {
        title?: string;
        initial?: string;
        stopGenerating?: string;
        regenerateResponse?: string;
    };
    icons?: {
        openIcon?: ReactNode;
        closeIcon?: ReactNode;
        headerCloseIcon?: ReactNode;
        sendIcon?: ReactNode;
        activityIcon?: ReactNode;
    };
    className?: string;
}

/**
 * CopilotChat - A pre-styled CopilotChat component
 * This component provides a chat interface for the Copilot functionality
 */
export function CopilotChat({
    instructions,
    labels,
    icons,
    className,
}: CopilotChatProps) {
    return (
        <div style={copilotStyles} className={className}>
            <CopilotKitCopilotChat
                instructions={instructions}
                labels={{
                    title: labels?.title || "AI Assistant",
                    initial: labels?.initial || "How can I help you today?",
                    stopGenerating: labels?.stopGenerating || "Stop Generating",
                    regenerateResponse: labels?.regenerateResponse || "Regenerate Response",
                }}
                icons={icons}
            />
        </div>
    );
}

// Export the styles for custom implementations
export { copilotStyles };

// Re-export components and types from CopilotKit for easier access
export { CopilotKit };
export { useCopilotAction, useCopilotReadable };
export type { CopilotKitCSSProperties };
